import { EInvoiceAddressSchemeType } from '@components'

import { addDays } from 'date-fns'

import { Contact } from '@features/contacts/types/contact'
import { DEFAULT_CURRENCY } from '@features/invoices/constants/defaultCurrency'
import { DEFAULT_INVOICE_LANGUAGE } from '@features/invoices/constants/defaultInvoiceLanguage'
import { DEFAULT_INVOICE_PAYMENT_TERM } from '@features/invoices/constants/defaultInvoicePaymentTerm'
import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { InvoiceDiscountType } from '@features/invoices/enums/invoiceDiscountType'
import type { DisplayedPaymentMethods } from '@features/invoices/types/invoiceForm/displayedPaymentMethods'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { DEFAULT_QUOTE_VALIDITY_DAYS } from '@features/quotes/constants/defaultQuoteValidityDays'

import { PredefinedInvoiceValues } from '../../types/predefinedInvoiceValues'
import { getDefaultRowLine } from './getDefaultRowLine'
import { getInvoiceFormReceiverFromContact } from './getInvoiceFormReceiverFromContact'

interface GetInvoiceFormDefaultValuesOptions {
  defaultContact?: Contact
  isQuote?: boolean
  isRecurringInvoice?: boolean
  isStripeCardPaymentActive?: boolean
  predefinedInvoiceValues?: PredefinedInvoiceValues
}

export const getInvoiceFormDefaultValues = ({
  defaultContact,
  isQuote,
  isRecurringInvoice,
  isStripeCardPaymentActive,
  predefinedInvoiceValues,
}: GetInvoiceFormDefaultValuesOptions = {}): InvoiceForm => {
  const {
    currency,
    footerNote,
    invoiceNumber,
    invoiceNumberPrefix,
    language,
    message,
    paymentTerm,
    purchaseOrderNumber,
  } = predefinedInvoiceValues ?? {}

  const now = new Date()
  const defaultPaymentTerm = defaultContact?.paymentTermsDays ?? paymentTerm ?? DEFAULT_INVOICE_PAYMENT_TERM
  const defaultDueDateOffsetDays = isQuote ? DEFAULT_QUOTE_VALIDITY_DAYS : defaultPaymentTerm
  // A quote only announces how the future invoice can be paid, so the seller opts in per quote,
  // while an invoice - being the actual request for payment - shows every configured method.
  const shouldDisplayPaymentMethodsByDefault = !isQuote
  const defaultPaymentMethods: DisplayedPaymentMethods = {
    bank: shouldDisplayPaymentMethodsByDefault,
    offlinePayment: shouldDisplayPaymentMethodsByDefault,
    paypal: shouldDisplayPaymentMethodsByDefault,
    // Card payments are not a per-document choice: the toggle writes the organization-wide PayNow
    // status, so it has to mirror that status on every document type. Defaulting it to off on a
    // quote would show a card payment as disabled while the organization has it enabled, and
    // flipping it there would change the setting for every other document.
    stripeCardPayment: Boolean(isStripeCardPaymentActive),
  }
  const defaultContactLanguage =
    defaultContact?.invoicingLanguage && invoiceLanguageToLocale[defaultContact.invoicingLanguage]
  const shouldUpdateDefaultBuyerReference =
    defaultContact?.publicEntityRoutingEnabled && defaultContact?.eInvoiceScheme === EInvoiceAddressSchemeType.LWID

  return {
    buyerReference: shouldUpdateDefaultBuyerReference ? defaultContact.ean : undefined,
    currency: defaultContact?.currencyId || currency || DEFAULT_CURRENCY,
    discount: 0,
    discountType: InvoiceDiscountType.Percentage,
    displayedPaymentMethods: defaultPaymentMethods,
    dateOfSaleOrService: isQuote || isRecurringInvoice ? null : now,
    dueDate: addDays(now, defaultDueDateOffsetDays),
    footerNote: footerNote || '',
    invoiceLines: [getDefaultRowLine(predefinedInvoiceValues)],
    invoiceNumber,
    invoiceNumberPrefix,
    issueDate: now,
    language: defaultContactLanguage || language || invoiceLanguageToLocale[DEFAULT_INVOICE_LANGUAGE],
    message: message || '',
    paymentTerm: defaultPaymentTerm,
    purchaseOrderNumber,
    receiver: defaultContact ? getInvoiceFormReceiverFromContact(defaultContact) : null,
  }
}
